import React, { ReactNode, Ref, forwardRef, useMemo } from 'react';
import { Placement } from '@floating-ui/react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  SelectContainer,
  Input,
  Icon,
  Value,
  ValueIcon,
  ValueText,
  SelectClear,
} from './select.styles';
import {
  Dropdown,
  DropdownIcon,
  DropdownItem,
  DropdownItems,
} from '../dropdown/dropdown.styles';
import withCloseOnOutsideClick, {
  WithCloseOnOutsideClickProps,
} from '../../HOCs/withCloseOnOutsideClick.hoc';
import { ReactComponent as ArrowDownIcon } from '../../assets/arrow-down-icon.svg';
import Button from '../button/button.component';
import { ButtonIconGlyph } from '../button/icons';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../popover/popover.component';
import FieldLabel from '../input/field-label.component';

export interface IDropdownItem<T> {
  label: string;
  value: T;
  icon?: React.ReactNode;
  glyph?: ButtonIconGlyph;
}

interface Props<T extends string> extends WithCloseOnOutsideClickProps {
  name: string; // formik field name
  width?: string;
  items: IDropdownItem<T>[];
  placeholder?: string;
  isOptional?: boolean;
  optionalText?: string;
  labelEndElement?: React.ReactNode;
  infoHint?: string;
  dropdownPosition?: Placement;
  onChange?: (item: IDropdownItem<T>) => void;
  onClear?: () => void;
  showClear?: boolean;
  title?: ReactNode;
  shouldMatchWidthOfInput?: boolean;
  isDisabled?: boolean;
  tabIndex?: number;
}

function FormSelectField<T extends string>(
  {
    name,
    isOpened,
    setIsOpened,
    items,
    onChange,
    onClear,
    showClear,
    width,
    isOptional,
    optionalText,
    labelEndElement,
    infoHint,
    dropdownPosition = 'bottom-start',
    placeholder,
    title,
    tabIndex,
    shouldMatchWidthOfInput = false,
    isDisabled = false,
  }: Props<T>,
  ref: Ref<HTMLDivElement>
) {
  const { getFieldMeta, setFieldValue } = useFormikContext();

  const fieldMeta = getFieldMeta(name);

  const selectedItem = useMemo(
    () => items.find((x) => x.value === fieldMeta.value),
    [items, fieldMeta.value]
  );

  const { t } = useTranslation();

  const handleChange = (item: IDropdownItem<T>) => {
    setIsOpened(false);
    setFieldValue(name, item.value);
    onChange && onChange(item);
  };

  const handleClear = () => {
    setIsOpened(false);
    setFieldValue(name, null);
    onClear && onClear();
  };

  return (
    <SelectContainer
      className='flag-emoji-font'
      $inputAppearence='button'
      $isOpened={isOpened}
      ref={ref}>
      <Popover
        shouldMatchReferenceWidth={shouldMatchWidthOfInput}
        placement={dropdownPosition}>
        <FieldLabel
          isHidden={!title}
          isOptional={isOptional}
          optionalText={optionalText}
          labelEndElement={labelEndElement}
          hint={infoHint}>
          {title}
        </FieldLabel>
        <PopoverTrigger asChild>
          <Input
            tabIndex={tabIndex}
            disabled={isDisabled}
            $width={width}
            onClick={() => setIsOpened((prev) => !prev)}>
            <Value>
              {selectedItem?.icon && (
                <ValueIcon>{selectedItem?.icon}</ValueIcon>
              )}
              <ValueText>
                {t(selectedItem?.label || placeholder || '')}
              </ValueText>
            </Value>
            {!isDisabled && (
              <Icon>
                <ArrowDownIcon />
              </Icon>
            )}
          </Input>
        </PopoverTrigger>

        <PopoverContent>
          <Dropdown>
            <DropdownItems>
              {items.map((item, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <PopoverClose key={i}>
                  <DropdownItem
                    type='button'
                    $isSelected={item.value === selectedItem?.value}
                    onClick={() => handleChange(item)}>
                    {item.icon && <DropdownIcon>{item.icon}</DropdownIcon>}
                    {t(item.label)}
                  </DropdownItem>
                </PopoverClose>
              ))}
            </DropdownItems>
            {showClear && (
              <SelectClear>
                <PopoverClose>
                  <Button
                    contentStyle='thin'
                    appearance='text'
                    onClick={handleClear}>
                    {t('clear')}
                  </Button>
                </PopoverClose>
              </SelectClear>
            )}
          </Dropdown>
        </PopoverContent>
      </Popover>
    </SelectContainer>
  );
}

export default withCloseOnOutsideClick(forwardRef(FormSelectField));
